import { browserMemberLabel } from "@/lib/browser-tool-labels";

interface ToolCatalogEntry {
	/** 実行時のツール名。 */
	toolName: string;
	/** policy はサーバのポリシー対象、client はポリシーに書けないクライアント専用ツール。 */
	kind: "policy" | "client";
	/** ポリシー JSON 上の表記。実行時のツール名と違うものだけ指定する。 */
	policyName?: string;
	/** トランスクリプトと承認カードで使う日本語表示名。 */
	label?: string;
	preparingLabel?: string;
	/** argPattern を指定できるツール。 */
	allowsArgPattern?: boolean;
}

/**
 * ツール 1 件 = レコード 1 件。kind が "policy" のレコードはサーバの ToolPolicyCatalog のツールと
 * 1 対 1 で対応させる (ここが唯一の同期ポイント)。ポリシー表記の導出は "policy" のレコードだけを対象にする。
 */
const TOOL_CATALOG: readonly ToolCatalogEntry[] = [
	{ toolName: "request_user_choice", kind: "client", preparingLabel: "質問を準備中" },
	{
		toolName: "bash",
		kind: "policy",
		policyName: "Bash",
		label: "Bash",
		allowsArgPattern: true,
	},
	{ toolName: "str_replace_based_edit_tool", kind: "policy", label: "ファイル編集" },
	{ toolName: "glob", kind: "policy", label: "ファイルを検索" },
	{ toolName: "grep", kind: "policy", label: "内容を検索" },
	{ toolName: "view_image", kind: "policy", label: "画像を表示" },
	{ toolName: "view_document", kind: "policy", label: "文書を読み込み" },
	{ toolName: "open_file", kind: "policy", label: "ファイルを開く" },
	{ toolName: "web_search", kind: "policy", label: "Web 検索" },
	{ toolName: "web_fetch", kind: "policy", label: "Web ページを取得" },
	{ toolName: "run_subagent", kind: "policy", label: "サブエージェント" },
	{ toolName: "run_workflow", kind: "policy", label: "ワークフロー" },
	{ toolName: "analyze_video", kind: "policy", label: "動画分析" },
	{ toolName: "inspect_lerobot_dataset", kind: "policy", label: "データセットを検査" },
	{ toolName: "infer_lerobot_feature_semantics", kind: "policy", label: "特徴量の意味を推定" },
	{ toolName: "submit_model_training", kind: "policy", label: "学習ジョブを投入" },
	{ toolName: "list_model_training_jobs", kind: "policy", label: "学習ジョブ一覧" },
	{ toolName: "check_model_training_status", kind: "policy", label: "学習ジョブの状態を確認" },
	{ toolName: "cancel_model_training", kind: "policy", label: "学習ジョブをキャンセル" },
	{ toolName: "import_training_artifacts", kind: "policy", label: "学習成果物を取り込み" },
	{
		toolName: "start_monitor",
		kind: "policy",
		label: "モニターを開始",
		preparingLabel: "モニターを開始中",
		allowsArgPattern: true,
	},
	{
		toolName: "stop_monitor",
		kind: "policy",
		label: "モニターを停止",
		preparingLabel: "モニターを停止中",
	},
	{
		toolName: "list_monitors",
		kind: "policy",
		label: "モニターの一覧",
		preparingLabel: "モニターの一覧",
	},
	{ toolName: "knowledge_graph_search", kind: "policy", label: "ナレッジグラフを検索" },
	{ toolName: "knowledge_graph_read", kind: "policy", label: "ナレッジグラフの記事を読み込み" },
	{ toolName: "knowledge_graph_create", kind: "policy", label: "ナレッジグラフの記事を作成" },
	{ toolName: "knowledge_graph_edit", kind: "policy", label: "ナレッジグラフの記事を編集" },
	{ toolName: "knowledge_graph_delete", kind: "policy", label: "ナレッジグラフの記事を削除" },
	{ toolName: "request_segmentation", kind: "client", label: "動画分析の確認" },
	// browser toolset の 31 メンバーはポリシー上 1 ツール。実行時のメンバー名は browser-tool-labels.ts が担う。
	{ toolName: "browser", kind: "policy", label: "ブラウザ", preparingLabel: "ブラウザを操作中" },
];

const POLICY_TOOLS = TOOL_CATALOG.filter((entry) => entry.kind === "policy");

function policyNameOf(entry: ToolCatalogEntry): string {
	return entry.policyName ?? entry.toolName;
}

/** ポリシー JSON に書けるツール表記。大文字小文字を含め完全一致で判定する。 */
export const POLICY_TOOL_NAMES: ReadonlySet<string> = new Set(
	POLICY_TOOLS.map((entry) => policyNameOf(entry)),
);

/** argPattern を書けるツール表記。 */
export const ARG_PATTERN_TOOL_NAMES: ReadonlySet<string> = new Set(
	POLICY_TOOLS.filter((entry) => entry.allowsArgPattern === true).map((entry) =>
		policyNameOf(entry),
	),
);

/** 実行時のツール名を JSON 上の表記へ写す。承認カードの表示にも使う。 */
export function policyToolName(toolName: string): string {
	return POLICY_TOOLS.find((entry) => entry.toolName === toolName)?.policyName ?? toolName;
}

/** JSON 上の表記を実行時のツール名へ写す。 */
export function runtimeToolName(policyName: string): string {
	return POLICY_TOOLS.find((entry) => policyNameOf(entry) === policyName)?.toolName ?? policyName;
}

/** 日本語表示名。表示名を持たないツールは実行時のツール名をそのまま返す。 */
export function toolDisplayName(toolName: string): string {
	return TOOL_CATALOG.find((entry) => entry.toolName === toolName)?.label ?? toolName;
}

/** ファイル編集ツールのコマンドごとの動作名。 */
export function editorCommandLabel(command: unknown): string | null {
	switch (command) {
		case "view":
			return "ファイルを読み込み";
		case "str_replace":
		case "insert":
			return "ファイルを編集";
		case "create":
			return "ファイルを作成";
		default:
			return null;
	}
}

/** 生成開始時の表示文言。名前のない旧通知は共通表示へ戻す。browser toolset のメンバーは動作名で見せる。 */
export function toolPreparingLabel(toolName?: string | null): string {
	if (!toolName) return "ツールの呼び出しを準備中";
	return (
		TOOL_CATALOG.find((entry) => entry.toolName === toolName)?.preparingLabel ??
		`${browserMemberLabel(toolName) ?? toolDisplayName(toolName)}：準備中`
	);
}
